import React, { useMemo } from 'react';
import { songs } from '../data/songs';
import { type Song } from '../types';
import { Button, ComboBox } from './core';
import { checkAnswer, getPossibleAnswers, removeUsedAnswers } from '../game';
import { useGame } from '../providers/GameProvider/GameProvider';
import { ModalHeader, ModalContent, Modal, ModalFooter } from './Modal';
import { type Category } from '../data/categories';
import { AnswersList } from './AnswersList';

interface AnswerModalProps {
  isOpen: boolean;
  onClose(): void;
  question1: Category;
  question2: Category;
  questionId: string;
}

export function AnswerModal({
  isOpen,
  onClose,
  question1,
  question2,
  questionId,
}: AnswerModalProps) {
  const [selectedAnswer, setSelectedAnswer] = React.useState<Song | undefined>(
    undefined
  );
  const [isInvalid, setIsInvalid] = React.useState(false);
  const [hasRevealedAnswers, setHasRevealedAnswers] = React.useState(false);

  const { handleWrongAnswer, handleCorrectAnswer, usedAnswers, gameState } =
    useGame();

  const options = useMemo(
    () => removeUsedAnswers([...songs], usedAnswers),
    [usedAnswers]
  );

  function handleClose() {
    setHasRevealedAnswers(false);
    onClose();
  }

  function handleSelect(value: string) {
    setIsInvalid(false);
    setSelectedAnswer(value as Song);
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!selectedAnswer) {
      setIsInvalid(true);

      return;
    }

    if (checkAnswer(question1, question2, selectedAnswer)) {
      handleCorrectAnswer(questionId, selectedAnswer);
    } else {
      handleWrongAnswer();
    }

    handleClose();
  }

  function handleRevealAnswers() {
    setHasRevealedAnswers(true);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      scrollable={hasRevealedAnswers}
    >
      <ModalHeader>
        {hasRevealedAnswers ? 'You could have had...' : 'Take a guess'}
      </ModalHeader>

      {hasRevealedAnswers ? (
        <ModalContent scrollable>
          <AnswersList answers={getPossibleAnswers(question1, question2)} />
        </ModalContent>
      ) : (
        <ModalContent>
          <form id="answer-form">
            <ComboBox
              label={`${question1} + ${question2}`}
              onSelect={handleSelect}
              options={options}
              placeholder="Search songs..."
            />
          </form>
        </ModalContent>
      )}

      <ModalFooter>
        {!hasRevealedAnswers && (
          <Button
            disabled={isInvalid || !selectedAnswer}
            onClick={handleSubmit}
            type="submit"
            form="answer-form"
          >
            Submit
          </Button>
        )}

        {gameState === 'continue' && !hasRevealedAnswers && (
          <Button onClick={handleRevealAnswers}>Reveal answers</Button>
        )}

        <Button onClick={handleClose}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
}
